import React, { useEffect, useState } from "react";
import { Search, Loader2, Ship, Quote, X, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Modal, reviewsApi, inputCls, btnCls, fmtDate, Stars } from "./shared";

export default function MatchModal({ review, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [garsonMode, setGarsonMode] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [manualG, setManualG] = useState("");

  useEffect(() => { setQ(review?.booking_ref || review?.reviewer_name || ""); setResults([]); setGarsonMode(""); setManualG(""); }, [review]);

  useEffect(() => {
    if (!review) { setResults([]); return; }
    if (garsonMode) {
      const t = setTimeout(async () => {
        setLoading(true);
        try { setResults((await reviewsApi.bookingSearch("", { garson: garsonMode, near: review.review_date || "" })).bookings || []); } catch { setResults([]); }
        setLoading(false);
      }, 50);
      return () => clearTimeout(t);
    }
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { setResults((await reviewsApi.bookingSearch(q.trim())).bookings || []); } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, review, garsonMode]);

  const pick = async (b) => {
    setSaving(b.id);
    try {
      await reviewsApi.match(review.id, { booking_id: b.id, confirmation_code: b.confirmation_code });
      toast.success(`Yorum ${b.confirmation_code || "#" + b.id} ile eşleştirildi`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eşleştirme başarısız"); }
    setSaving(null);
  };

  const assignGarson = async (g) => {
    const name = (g || "").trim();
    if (!name) return;
    setAssigning(true);
    try {
      await reviewsApi.assignGarson(review.id, name);
      toast.success(`Yorum "${name}" garsonuna atandı (rezervasyonsuz)`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Atama başarısız"); }
    setAssigning(false);
  };

  if (!review) return null;
  return (
    <Modal open onClose={onClose} title="Yorumu Rezervasyonla Eşleştir" width="max-w-2xl" testId="match-modal">
      <div className="mb-3 text-xs text-slate-400 bg-slate-800/70 border border-slate-700 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1"><Stars value={review.rating} /> <span className="text-slate-200 font-medium">{review.reviewer_name || "(isimsiz)"}</span> · {fmtDate(review.review_date)}</div>
        <div className="line-clamp-2">{review.body}</div>
      </div>
      {review.text_garsons?.length > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap text-xs" data-testid="match-garson-hint">
          <span className="inline-flex items-center gap-1 text-emerald-300 font-medium"><Quote className="w-3.5 h-3.5" /> Yorumda geçen garson:</span>
          {review.text_garsons.map(g => (
            <button key={g} type="button" onClick={() => setGarsonMode(garsonMode === g ? "" : g)}
              className={`px-2.5 py-1 rounded-full border transition-colors ${garsonMode === g ? "bg-emerald-600 border-emerald-600 text-white" : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"}`}
              data-testid={`match-garson-chip-${g}`}>{g}</button>
          ))}
          <span className="text-slate-500">— tarihe en yakın rezervasyonları gösterir</span>
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input autoFocus value={q} onChange={e => { setQ(e.target.value); setGarsonMode(""); }} placeholder="Booking ref (GYG…, BR-…), müşteri adı, telefon…"
          className={`${inputCls} w-full pl-9 py-2`} data-testid="match-search-input" />
      </div>
      {garsonMode && (
        <div className="mt-2 flex items-center gap-2 text-xs text-emerald-300">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/40">Garson: {garsonMode} · {fmtDate(review.review_date)} civarı</span>
          <button type="button" onClick={() => setGarsonMode("")} className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-200" data-testid="match-garson-clear"><X className="w-3 h-3" /> kaldır</button>
        </div>
      )}
      <div className="mt-3 max-h-[360px] overflow-y-auto space-y-1.5" data-testid="match-results">
        {loading && <div className="flex items-center gap-2 text-xs text-slate-500 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Aranıyor…</div>}
        {!loading && q.trim().length >= 2 && results.length === 0 && <div className="text-xs text-slate-500 py-3">Rezervasyon bulunamadı</div>}
        {results.map(b => (
          <button key={b.id} onClick={() => pick(b)} disabled={!!saving}
            className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-700 bg-slate-800/60 hover:border-blue-500 hover:bg-slate-800 transition-colors disabled:opacity-50"
            data-testid={`match-result-${b.id}`}>
            <div className="font-mono text-xs text-sky-300 w-[130px] shrink-0">{b.confirmation_code || "#" + b.id}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{b.customer_name || "(isimsiz)"} <span className="text-slate-500 text-xs">· {b.total_pax} pax</span></div>
              <div className="text-[11px] text-slate-400 truncate">{b.product_name}</div>
            </div>
            <div className="text-right shrink-0 text-xs">
              <div className="text-slate-200">{fmtDate(b.booking_date)} {b.booking_time?.slice(0, 5)}</div>
              <div className="text-slate-500 flex items-center justify-end gap-1">{b.boat_name && <><Ship className="w-3 h-3" />{b.boat_name}</>}{b.garson && <span className="text-emerald-300 ml-1">{b.garson}</span>}</div>
            </div>
            {saving === b.id && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
          </button>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="assign-garson-box">
        <div className="text-xs font-semibold text-amber-200 flex items-center gap-1.5 mb-1"><UserRound className="w-3.5 h-3.5" /> Müşteriyi/rezervasyonu bulamadın mı? Yorumu doğrudan garsona ata</div>
        <div className="text-[11px] text-amber-200/70 mb-2">Rezervasyonla eşleşmez; yorum garsonun toplam yorum ve ortalamasına sayılır, bekleyen listeden çıkar.</div>
        <div className="flex items-center gap-2 flex-wrap">
          {(review.text_garsons || []).map(g => (
            <button key={g} type="button" disabled={assigning} onClick={() => assignGarson(g)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold disabled:opacity-50"
              data-testid={`assign-garson-chip-${g}`}>{assigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserRound className="w-3 h-3" />}{g} olarak ata</button>
          ))}
          <div className="flex items-center gap-1">
            <input value={manualG} onChange={e => setManualG(e.target.value)} placeholder="veya garson adı yaz…"
              className={`${inputCls} py-1 text-xs w-40`} data-testid="assign-garson-manual-input" />
            <button type="button" disabled={assigning || !manualG.trim()} onClick={() => assignGarson(manualG)}
              className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold disabled:opacity-40" data-testid="assign-garson-manual-btn">Ata</button>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={onClose} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`} data-testid="match-cancel-btn">Kapat</button>
      </div>
    </Modal>
  );
}
